import { type ReactNode, useCallback } from 'react';

import { useFeatureFlag } from '@spotify/confidence-flag-evaluation-react';
import {
  AssistantMessage,
  type InlineMarkerBlock,
  type InlineNumberBlock,
  createChatEventExtension,
} from '@spotify/plugin-confidence-chat-react';
import {
  PULSE_BLOCK_TYPE,
  PulseNumberHighlight,
  SaveToPulseButton,
} from '@spotify/plugin-confidence-pulse-react';

import { confidenceChatPlugin } from '../plugin';

const getSourceChatName = (eventName?: string): string | undefined => {
  const eventPathIndex = eventName?.indexOf('/events/') ?? -1;
  return eventPathIndex >= 0 ? eventName?.slice(0, eventPathIndex) : undefined;
};

const getBlockPulseInput = (block: InlineMarkerBlock) => {
  if (block.type === PULSE_BLOCK_TYPE.TABLE) {
    return {
      suggestedName: block.table.name || 'Table',
      blockType: PULSE_BLOCK_TYPE.TABLE,
    };
  }
  if (block.type === PULSE_BLOCK_TYPE.TEXT) {
    return {
      suggestedName: block.name || 'Text',
      blockType: PULSE_BLOCK_TYPE.TEXT,
      selectedContent: block.content,
    };
  }
  return {
    suggestedName: block.name || 'Chart',
    blockType: PULSE_BLOCK_TYPE.CHART,
    visualizationSpec: block.spec,
  };
};

const AssistantMessageWithBlocks = ({
  content,
  eventName,
}: {
  content: string;
  eventName?: string;
}) => {
  const { enabled: pulsesEnabled } = useFeatureFlag('pulses', {
    enabled: false,
  });

  const renderBlockActions = useCallback(
    (block: InlineMarkerBlock) => {
      if (!pulsesEnabled) return null;
      const sourceChat = getSourceChatName(block.eventName);
      if (!sourceChat) return null;

      return (
        <SaveToPulseButton
          input={{
            sourceChat,
            sourceEvent: block.eventName,
            ...getBlockPulseInput(block),
          }}
        />
      );
    },
    [pulsesEnabled],
  );

  const renderInlineNumber = useCallback(
    (block: InlineNumberBlock, value: ReactNode) => {
      if (!pulsesEnabled) return value;
      const sourceChat = getSourceChatName(block.eventName);
      if (!sourceChat) return value;

      return (
        <PulseNumberHighlight
          name={block.name}
          value={block.value}
          sourceInput={{ sourceChat, sourceEvent: block.eventName }}
        >
          {value}
        </PulseNumberHighlight>
      );
    },
    [pulsesEnabled],
  );

  return (
    <AssistantMessage
      content={content}
      eventName={eventName}
      renderBlockActions={pulsesEnabled ? renderBlockActions : undefined}
      renderInlineNumber={pulsesEnabled ? renderInlineNumber : undefined}
    />
  );
};

export const AssistantMessageEventExtension = confidenceChatPlugin.provide(
  createChatEventExtension({
    id: 'assistant-message',
    predicate: event => Boolean(event.assistantMessage) && !event.error,
    render: event => {
      const message = event.assistantMessage;
      if (!message) return null;
      return (
        <AssistantMessageWithBlocks
          content={message.content}
          eventName={event.name}
        />
      );
    },
  }),
);
